import {
  Badge,
  ButtonGroup,
  Center,
  Code,
  Spacer,
  useToast,
} from '@chakra-ui/react';
import React, { useMemo, useEffect, useState, useCallback } from 'react';

import { useGetBorrowingBasePipelineArtifactDocumentsQuery } from '../requests/tapeBuilder.gql.generated';
import {
  TapeBuilderDiffDocument,
  DiffIconButton,
} from './TapeBuilderDiffDocument';
import { Select } from 'packages/common-frontend/components/Select';
import { TextWithCopy } from 'packages/common-frontend/components/TextWithCopy';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';

type Props = {
  pipelineDefinitionId: string;
  onDocumentSelected: (documentId: string) => void;
  diff: TapeBuilderDiffDocument | null;
  setDiff: (diff: TapeBuilderDiffDocument | null) => void;
};
export const ViewModeControls = ({
  pipelineDefinitionId,
  diff,
  setDiff,
  onDocumentSelected,
}: Props) => {
  const toast = useToast();
  const [selectedArtifactDocumentId, setSelectedArtifactdocumentId] = useState<
    string | null
  >(null);

  const {
    data: artifactDocumentsData,
    loading: loadingArtifactDocuments,
  } = useGetBorrowingBasePipelineArtifactDocumentsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    variables: { pipelineDefinitionId },
  });

  const artifactDocuments = useMemo(() => {
    return artifactDocumentsData?.borrowingBasePipelineArtifactDocuments || [];
  }, [artifactDocumentsData]);
  const artifactDocumentOptions = artifactDocuments.map((artifactDocument) => ({
    value: artifactDocument.id,
    label: `version-${artifactDocument.version} ${
      artifactDocument.isWip ? '(WIP)' : ''
    }`,
  }));

  useEffect(() => {
    if (artifactDocuments.length > 0) {
      setSelectedArtifactdocumentId(artifactDocuments[0].id);
    }
  }, [artifactDocuments]);

  const getDiffDocument = useCallback(() => {
    const selectedDocument = artifactDocuments.find(
      (artifactDocument) => artifactDocument.id === selectedArtifactDocumentId,
    );

    if (!selectedDocument) {
      return null;
    }

    // find the version before the selectedArtifactDocumentId
    const previousDocument = artifactDocuments.find(
      (artifactDocument) =>
        artifactDocument.version === selectedDocument?.version - 1,
    );

    if (!previousDocument) {
      return null;
    }
    return { oldValue: previousDocument, newValue: selectedDocument };
  }, [artifactDocuments, selectedArtifactDocumentId]);

  useEffect(() => {
    if (diff) {
      setDiff(getDiffDocument());
    }
    // we want to update diff document if selectedArtifactDocumentId changes
    // we reference diff iin the above function but we do not want to
    // take dependency on diff, as that would cause infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDiffDocument, selectedArtifactDocumentId, setDiff]);

  const newDiff = getDiffDocument();

  if (loadingArtifactDocuments) {
    return (
      <>
        <Spacer />
        <Badge ml="2" variant="outline" colorScheme="yellow">
          loading artifact documents...
        </Badge>
      </>
    );
  }

  return (
    <>
      <ButtonGroup ml="10px">
        <Select<string>
          value={selectedArtifactDocumentId || undefined}
          onChange={(value) => {
            if (value) {
              setSelectedArtifactdocumentId(value);
              onDocumentSelected(value);
            }
          }}
          options={artifactDocumentOptions}
        />
        <Center>
          <DiffIconButton diff={diff} setDiff={setDiff} newDiff={newDiff} />
        </Center>
      </ButtonGroup>
      <Code ml="2" mr="2" alignItems="center">
        <TextWithCopy label="Copy selected ArtifactDocumentId">
          {selectedArtifactDocumentId}
        </TextWithCopy>
      </Code>
    </>
  );
};
